<template>
  <div class="booking-payment">
    <Headline :value="data.headline" :number="data.sectionNumber" />

    <div class="booking-payment__methods">
      <PaymentMethod v-for="(option, index) in data.paymentMethodTiles" :key="option.Id"
        :selected="dropdowns.paymentMethod[index]" :initialAvailable="dropdowns.tilesInitialAvailable"
        :name="option.Header" :description="option.Content" :details="option.Description"
        :options="paymentMethods" :anyVisible="isAnyDropdownVisible()" :uiActionId="option.UI_Action_Id"
        :paymentTypeId="option.Payment_Type_Id" :isTraditionalTransferBlocked="data.isTraditionalTransferBlocked" tabindex="0"
        @input="selectBank" @click="select(option, index)" />
    </div>

    <div v-if="!isMobile" v-show="details.UI_Action_Id == 'select-card'" class="booking-payment__details">
      <div class="booking-payment__details--description">
        <img src="assets/images/icons_payments/logo_145.png" class="card-operator">
        <p v-html="details.Description" class="booking-payment__details--lower booking-payment__details--blue" />
      </div>
    </div>

    <div v-if="!isMobile" v-show="details.UI_Action_Id == 'select-bank'" class="booking-payment__details">
      <div v-html="details.Description" class="booking-payment__details--bank-selection" />
      <ul class="details-list">
        <li v-for="option in paymentMethods" :key="option.PaymentTypeId"
          class="details-list__bank" @click="selectBank(option.PaymentTypeId, option.Name)" tabindex="0"
          :class="{ 'details-list__bank--not-chosen': paymentMethod != option.PaymentTypeId}">
          <img :src="`assets/images/icons_payments/logo_${option.PaymentTypeId}.png`" :alt="option.Name" :title="option.Name">
        </li>
      </ul>
    </div>

    <div v-if="!isMobile" v-show="details.UI_Action_Id == 'standard'" class="booking-payment__details">
      <p v-html="details.Description" class="booking-payment__details--lower booking-payment__details--blue" />
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import Component from 'vue-class-component'
import { mapActions, mapGetters } from 'vuex'

import PaymentMethod from '@/components/booking/PaymentMethod'
import Headline from '@/components/forms/Headline'
import DropdownHandler from '@/components/search/DropdownHandler'
import SelectBox from '@/components/search/SelectBox'
import { DataLayerEvent, pushEvent } from '@/modules/datalayer/helpers'
import Store from '@/store/index'
import { SET_PAYMENT_METHOD } from '@/store/mutation-types'
import MediaDetector from '@/utils/MediaDetector'

@Component({
  store: Store,
  mixins: [
    MediaDetector,
    DropdownHandler,
  ],
  components: {
    Headline,
    PaymentMethod,
    SelectBox,
  },
  props: {
    data: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapGetters({
      paymentMethod: 'Booking/getPaymentMethod',
    }),
  },
  methods: {
    ...mapActions({
      setPaymentMethod: `Booking/${SET_PAYMENT_METHOD}`,
    }),
  },
})
export default class BookingPaymentPicker extends Vue {
  details = {}
  dropdowns = {
    paymentMethod: this.data.paymentMethodTiles.map(() => false),
    tilesInitialAvailable: true,
  }

  get paymentMethods () {
    return this.data.paymentMethods.filter(this.duplicatedPaymentMethods)
  }

  get bankSelectionActive () {
    return this.details.UI_Action_Id == 'select-bank'
  }

  duplicatedPaymentMethods (method) {
    return this.data.paymentMethodTiles.every(option => option.Payment_Type_Id != method.PaymentTypeId)
  }

  toggleMethod (index) {
    this.dropdowns.paymentMethod = this.dropdowns.paymentMethod.map(() => false)
    this.toggle('paymentMethod', index)
  }

  select (option, index) {
    this.toggleMethod(index)
    this.details = option
    this.dropdowns.tilesInitialAvailable = false

    this.selectBank(option.Payment_Type_Id, option.Header)
  }

  selectBank (selected, name = undefined) {
    this.setPaymentMethod(selected.toString())
    /* eslint-disable */
    const [ event ] = DataLayerEvent.payment({
      item_name: window.offer_data.HotelName,
      item_id: window.offer_data.OfferCode,
      quantity: window.offer_data.Persons.length,
      price: window.offer_data.TotalPrice / window.offer_data.Persons.length,
      currency: 'PLN',
    }, name || selected.toString())
    pushEvent(event)
  /* eslint-enable */
  }
}
</script>

<style lang="scss" scoped>
.booking-payment {
  padding: 20px;

  @include mediaFluid(tablet) {
    padding: 0 0 20px;
  }

  @include mediaFluid(desktop) {
    padding: 0;
  }

  &__methods {
    display: flex;
    flex-direction: column;
    width: 100%;

    @include mediaFluid(tablet) {
      flex-direction: row;
      justify-content: space-between;
    }

    @include mediaFluid(desktop) {
      display: inline-block;
    }
  }

  &__details {
    margin: 16px 0;

    &--description {
      width: 660px;
      display: flex;
      align-items: center;
    }

    &--lower {
      font-family: 'Gotham Book';
      font-size: 12px;
      font-weight: 100;
      line-height: 1.2em;
      width: 670px;
    }

    &--blue {
      color: #005CA9;
      line-height: 1.4em;
    }

    &--main {
      color: #005CA9;
      font-size: 12px;
      margin-bottom: 5px;
    }
  }

  .card-operator {
    width: 140px;
    height: 100%;
    margin-right: 25px;
    flex: 1 0 auto;
  }

  .details-list {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-start;
    list-style-type: none;
    padding: 0;
    margin-top: 20px;
    
    &__bank {
      display: flex;
      align-items: center;
      justify-content: center;
      width: calc(99% / 7);
      height: 70px;
      background: white;
      margin: 0 1px 1px 0;
      cursor: pointer;
      outline: none;

      &--not-chosen {
        opacity: .5;
      }

      &:nth-child(7n) {
        margin-right: 0;
      }

      &:hover {
          box-shadow: 0px 0px 4px 1px rgba(#000000, .15)
      }

      img {
        max-height: 100%;
        max-width: 100%;
        padding: 0 10px;
      }
    }
  }
}
</style>

<style lang="scss">
.booking-payment {
  .headline {
    font-family: 'Gotham Book';

    &__number {
      margin-left: 4px;
    }

    span {
      line-height: 1.2;
      font-size: 1.3em;
      font-weight: bold;

      @include media(tablet) {
        line-height: 1;
        font-size: 22px;
        font-weight: lighter;
      }
    }
  }

  &__details {
    a {
      text-decoration: underline;
      text-decoration-color: rgba(0, 92, 169, 0.3);
    }

    b, strong {
      font-family: 'Gotham';
    }
  }

  &__details--bank-selection {
    h1 {
      color: #005CA9;
      font-size: 12px;
      text-transform: uppercase;
      margin-bottom: 7px;
    }

    p:nth-child(2) {
      font-family: 'Gotham Book';
      font-size: 12px;
      color: #005CA9;
      margin-bottom: 5px;
    }

    p:last-child {
      font-size: 12px;
      font-weight: 100;
      line-height: 1.2em;
      width: 670px;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/BookingPaymentPicker.vue